import React, { useState, useEffect, useRef, useCallback } from "react";
import ReactDOM from "react-dom";
import { useTranslation } from "react-i18next";
import { getProducts } from "../../api/product";
import { AudiotracksCMS } from "../../types/types";
import Button from "../ui/Button";
import TextInput from "../ui/TextInput";
import logo from "../../assets/images/logo-rewind.png";
import studio from "./StudioModal.module.css";
import styles from "./ProductPickerModal.module.css";

interface ProductPickerModalProps {
    isOpen: boolean;
    onClose: () => void;
    onProductSelect: (productId: string) => void;
}

const ProductPickerModal: React.FC<ProductPickerModalProps> = ({ isOpen, onClose, onProductSelect }) => {
    const { t: rawT } = useTranslation();
    const t = rawT as any;
    const modalContentRef = useRef<HTMLDivElement>(null);

    const [products, setProducts] = useState<AudiotracksCMS[]>([]);
    const [filteredProducts, setFilteredProducts] = useState<AudiotracksCMS[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [searchTerm, setSearchTerm] = useState<string>("");

    const handleOutsideClick = useCallback(
        (e: MouseEvent) => {
            if (modalContentRef.current && !modalContentRef.current.contains(e.target as Node)) {
                onClose();
            }
        },
        [onClose],
    );

    useEffect(() => {
        if (isOpen) {
            document.addEventListener("mousedown", handleOutsideClick);
            fetchProducts();
        }
        return () => {
            document.removeEventListener("mousedown", handleOutsideClick);
        };
    }, [isOpen, handleOutsideClick]);

    const fetchProducts = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem("token");
            if (!token) {
                console.error("No token found");
                return;
            }
            const productsData = await getProducts({ token });
            setProducts(productsData);
            setFilteredProducts(productsData);
        } catch (error) {
            console.error("Error fetching products:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (searchTerm) {
            const filtered = products.filter(
                (product) =>
                    product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                    product.companies?.some((company) => company.name.toLowerCase().includes(searchTerm.toLowerCase())),
            );
            setFilteredProducts(filtered);
        } else {
            setFilteredProducts(products);
        }
    }, [searchTerm, products]);

    const handleProductSelect = (productId: string) => {
        onProductSelect(productId);
        onClose();
    };

    if (!isOpen) return null;

    const modal = (
        <div className={studio.overlay}>
            <div className={`${studio.content} ${styles.modalContent}`} ref={modalContentRef}>
                <div className={studio.logoWrap}>
                    <img src={logo} alt="Logo" />
                </div>
                <h1 className={studio.title}>{t("selectProduct")}</h1>

                <div className={styles.search}>
                    <TextInput
                        placeholder={t("searchProducts")}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        disabled={loading}
                    />
                </div>

                <div className={styles.content}>
                    {loading ? (
                        <div className={styles.loadingContainer}>
                            <div className="loading-spinner"></div>
                            <p>{t("loading")}...</p>
                        </div>
                    ) : (
                        <div className={styles.list}>
                            {filteredProducts.length === 0 ? (
                                <div className={styles.noProducts}>
                                    <p>{t("noProductsFound")}</p>
                                </div>
                            ) : (
                                filteredProducts.map((product) => (
                                    <div key={product.id} className={styles.item}>
                                        <div className={styles.itemInfo}>
                                            <h3 className={styles.itemName}>{product.name}</h3>
                                            <div className={styles.itemCompanies}>
                                                {product.companies?.map((company, index) => (
                                                    <span key={company.id} className={styles.companyTag}>
                                                        {company.name}
                                                        {index < product.companies!.length - 1 && ", "}
                                                    </span>
                                                ))}
                                            </div>
                                            {product.backgroundPicture && (
                                                <div className={styles.itemImage}>
                                                    <img src={product.backgroundPicture} alt={product.name} />
                                                </div>
                                            )}
                                        </div>
                                        <div className={styles.itemActions}>
                                            <Button
                                                variant="primary"
                                                label={t("copyLinkToProductAppPage")}
                                                onClick={() => handleProductSelect(product.id)}
                                            />
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    )}
                </div>

                <div className={studio.buttonsCentered}>
                    <Button variant="cancel" label={t("cancel")} onClick={onClose} />
                </div>
            </div>
        </div>
    );

    return ReactDOM.createPortal(modal, document.body);
};

export default ProductPickerModal;
